import type { Policy, PolicyVehicle, PolicyDriver } from "@shared/schema";
import { IN_FORCE_STATUSES } from "@shared/schema";
import {
  driverVariantAffinity,
  hasRealDriverValue,
  mergeDriverVariantFields,
  type FieldAffinity,
} from "@shared/driver-fields";

// CSR-1456 — shared fixed column widths so the Additional Insureds, Waivers of
// Subrogation, and Loss Payees asset tables line up column-for-column when
// stacked on the Assets tabs. Index order matches their shared header:
// Name, Email/Status, COIs Sent, Last Sent, Created At, Actions. Apply each
// width to the matching <TableHead> and set `table-fixed` on the <Table>.
export const ASSET_TABLE_COLUMN_WIDTHS = [
  "w-[28%]", // Name (+ address sub-line) — widest, holds long names/addresses
  "w-[22%]", // Email / status badge ("Pending Additional Information")
  "w-[10%]", // COIs Sent
  "w-[11%]", // Last Sent
  "w-[12%]", // Created At
  "w-[17%]", // Actions — right-aligned so the delete button lines up with the AI table's
] as const;

// CSR-2274 — the Additional Insureds table alone carries a Status column. It
// takes its width out of the Actions column, so the five leading columns and
// the right-aligned delete button still line up with the other two tables.
export const ADDITIONAL_INSURED_TABLE_COLUMN_WIDTHS = [
  ...ASSET_TABLE_COLUMN_WIDTHS.slice(0, 5),
  "w-[10%]", // Status
  "w-[7%]", // Actions
] as const;

export interface VehicleEntry {
  vehicle: PolicyVehicle;
  policyNumber: string;
  policyTermLabel?: string;
  policyId: string;
  policyCurrentOrUpcoming?: boolean;
  active: boolean;
  removedAt: string | null;
}

export interface DriverEntry {
  driver: PolicyDriver;
  policyNumber: string;
  policyTermLabel?: string;
  policyId: string;
  policyCurrentOrUpcoming?: boolean;
  active: boolean;
  removedAt: string | null;
  // CSR-1345 — every underlying roster row this display entry represents
  // (masked/unmasked variants collapse into one entry). References point at
  // the actual `policy.drivers` elements so the remove action can drop ALL
  // variant rows on a policy instead of leaving a masked twin behind.
  sourceRows: { policyId: string; driver: PolicyDriver }[];
}

const TRAILER_TRUCK_TYPE = "Trailer";

/**
 * A live policy term the agent still owns: one whose status is in force and
 * whose term has not ended. Deliberately includes a term that has not STARTED
 * yet.
 *
 * CSR-2203 — this used to also require `eff <= today`, which lumped a bound
 * new-business term together with a superseded one. Both then read as
 * historical: the insured Assets tab disabled every + Add button and badged
 * the term's assets Inactive. That is backwards — the week between binding and
 * the effective date is exactly when the agent loads the fleet. Only the upper
 * bound separates a live term from history.
 */
export function policyIsCurrentOrUpcoming(p: Policy): boolean {
  const status = (p.status?.toLowerCase() ?? "") as (typeof IN_FORCE_STATUSES)[number];
  if (!IN_FORCE_STATUSES.includes(status)) return false;
  if (!p.expirationDate) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const exp = new Date(p.expirationDate + "T00:00:00").getTime();
  return exp >= today;
}

export function policyTermLabel(p: Policy): string {
  const dates = p.effectiveDate && p.expirationDate
    ? `${p.effectiveDate} – ${p.expirationDate}`
    : "Term dates unavailable";
  return `${p.policyNumber} · ${dates}`;
}

/**
 * CSR-2335 — the Add dialog's default Related Policies selection: the only
 * term when there is one, else the single current/upcoming term, else nothing
 * (the agent picks). History terms are never pre-selected.
 */
export function defaultPolicySelection(policies: Policy[]): string[] {
  if (policies.length === 1) return [policies[0].id];
  const live = policies.filter(policyIsCurrentOrUpcoming);
  return live.length === 1 ? [live[0].id] : [];
}

// CSR-1093: cross-policy driver dedup key. Parallels the server-side
// `keyForDriver` (CSR-1016, `server/asset-merge.ts`) but cannot import it —
// that module is server-only. Server merge dedupes within one policy; this
// helper dedupes across policies. Same priority order keeps the symptom
// fixed in both layers: same physical driver synced from carriers with
// different licenseNumber shapes (masked, unmasked, empty) collapses to one
// row instead of rendering three.
// CSR-1148 exported this so the asset dialogs can match the same physical
// driver across policies for the multi-policy link picker. Same key the
// insured-level aggregate dedup uses.
export function keyForDriverDisplay(d: PolicyDriver): string | null {
  if (d.databaseId && typeof d.databaseId === "string") {
    const id = d.databaseId.trim().toLowerCase();
    if (id) return `id:${id}`;
  }
  const name = String(d.name ?? `${d.firstName ?? ""} ${d.lastName ?? ""}`)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (name) return `nm:${name}`;
  if (d.licenseNumber) return `ln:${d.licenseNumber}`;
  return null;
}

/**
 * CSR-1148 — cross-policy identity for a vehicle/trailer: the normalized VIN.
 * Returns null for VIN-less entries (they can't participate in multi-policy
 * linking by identity).
 */
export function vehicleIdentity(v: PolicyVehicle): string | null {
  if (v.vin && typeof v.vin === "string") {
    const n = v.vin.trim().toUpperCase();
    if (n) return n;
  }
  // VIN-less entries (e.g. Non-Owned Attached Trailers) can't cross-link by
  // identity, but `addedAt` is unique within a policy — enough for an edit to
  // locate and update its origin row instead of orphaning it.
  if (v.addedAt) return v.addedAt;
  return null;
}

interface AssetEntry {
  addedAt?: string;
  removedAt?: string | null;
}

/**
 * CSR-1148 — ids of the policies whose `arrayKey` JSONB array currently holds
 * an active (non-removed) entry matching `targetKey`. Used to pre-select the
 * multi-policy picker when editing an asset.
 */
export function policiesLinkedToAsset<T extends AssetEntry>(
  policies: Policy[],
  arrayKey: "vehicles" | "trailers" | "drivers",
  identityOf: (item: T) => string | null,
  targetKey: string,
): string[] {
  const ids: string[] = [];
  for (const p of policies) {
    const arr = (p[arrayKey] as T[] | null) ?? [];
    if (!Array.isArray(arr)) continue;
    const linked = arr.some(
      (item) => !item.removedAt && identityOf(item) === targetKey,
    );
    if (linked) ids.push(p.id);
  }
  return ids;
}

export interface AssetLinkWrite<T> {
  policyId: string;
  nextArray: T[];
}

/**
 * CSR-1148 — given the full set of policies on an insured and the desired set
 * of policies an asset should be linked to (`selectedPolicyIds`), compute the
 * per-policy JSONB array writes needed:
 *
 * - selected policy → upsert the asset (replace the matching entry in place
 *   preserving order, or append when absent), via `buildEntry`.
 * - de-selected policy that currently has an active match → drop every entry
 *   matching the asset identity (hard unlink, mirroring the per-row remove).
 * - de-selected policy without an active match → no write.
 *
 * `matchKey` is the identity used to find existing entries (the asset's
 * *original* identity, so a VIN edit still locates the rows to update/remove).
 * Only policies that need a write appear in the result.
 */
export function planAssetLinkWrites<T extends AssetEntry>(params: {
  policies: Policy[];
  arrayKey: "vehicles" | "trailers" | "drivers";
  identityOf: (item: T) => string | null;
  matchKey: string;
  buildEntry: (existing: T | null) => T;
  selectedPolicyIds: string[];
}): AssetLinkWrite<T>[] {
  const { policies, arrayKey, identityOf, matchKey, buildEntry, selectedPolicyIds } = params;
  const selected = new Set(selectedPolicyIds);
  const writes: AssetLinkWrite<T>[] = [];

  for (const p of policies) {
    const arr = ((p[arrayKey] as T[] | null) ?? []).slice();
    const matchIndex = arr.findIndex((item) => identityOf(item) === matchKey);
    const hasActiveMatch = arr.some(
      (item) => !item.removedAt && identityOf(item) === matchKey,
    );

    if (selected.has(p.id)) {
      const existing = matchIndex >= 0 ? arr[matchIndex] : null;
      const entry = buildEntry(existing);
      // Drop every entry for this identity (collapsing any active+removed
      // duplicates) and append the single canonical entry.
      const nextArray = [...arr.filter((item) => identityOf(item) !== matchKey), entry];
      writes.push({ policyId: p.id, nextArray });
    } else if (hasActiveMatch) {
      // Unlink: drop every entry (active or already-removed) for this identity.
      const nextArray = arr.filter((item) => identityOf(item) !== matchKey);
      writes.push({ policyId: p.id, nextArray });
    }
  }

  return writes;
}

/**
 * Dedupe vehicles across all policies by VIN, keeping the in-force / not-removed
 * entry when duplicated. Vehicles without a VIN are kept as-is (no dedupe key).
 * Sort order: active → inactive → removed.
 */
export function aggregateVehicles(policies: Policy[]): VehicleEntry[] {
  const seen = new Map<string, VehicleEntry>();
  const results: VehicleEntry[] = [];
  for (const p of policies) {
    const vehicles = p.vehicles as PolicyVehicle[] | null;
    if (!Array.isArray(vehicles)) continue;
    const policyCurrentOrUpcoming = policyIsCurrentOrUpcoming(p);
    for (const v of vehicles) {
      const removedAt = v.removedAt ?? null;
      const isActive = policyCurrentOrUpcoming && !removedAt;
      if (v.vin && seen.has(v.vin)) {
        const existing = seen.get(v.vin)!;
        if (preferEntry(existing, { active: isActive, removedAt, policyCurrentOrUpcoming })) {
          existing.vehicle = v;
          existing.policyNumber = p.policyNumber;
          existing.policyTermLabel = policyTermLabel(p);
          existing.policyId = p.id;
          existing.policyCurrentOrUpcoming = policyCurrentOrUpcoming;
          existing.active = isActive;
          existing.removedAt = removedAt;
        }
        continue;
      }
      const entry: VehicleEntry = {
        vehicle: v,
        policyNumber: p.policyNumber,
        policyTermLabel: policyTermLabel(p),
        policyId: p.id,
        policyCurrentOrUpcoming,
        active: isActive,
        removedAt,
      };
      if (v.vin) seen.set(v.vin, entry);
      results.push(entry);
    }
  }
  results.sort(sortByLifecycle);
  return results;
}

/**
 * Dedupe trailers across all policies by VIN, keeping the in-force / not-removed
 * entry when duplicated. Mirrors `aggregateVehicles` but reads `p.trailers`.
 */
export function aggregateTrailers(policies: Policy[]): VehicleEntry[] {
  const seen = new Map<string, VehicleEntry>();
  const results: VehicleEntry[] = [];
  for (const p of policies) {
    const trailers = p.trailers as PolicyVehicle[] | null;
    if (!Array.isArray(trailers)) continue;
    const policyCurrentOrUpcoming = policyIsCurrentOrUpcoming(p);
    for (const t of trailers) {
      const removedAt = t.removedAt ?? null;
      const isActive = policyCurrentOrUpcoming && !removedAt;
      if (t.vin && seen.has(t.vin)) {
        const existing = seen.get(t.vin)!;
        if (preferEntry(existing, { active: isActive, removedAt, policyCurrentOrUpcoming })) {
          existing.vehicle = t;
          existing.policyNumber = p.policyNumber;
          existing.policyTermLabel = policyTermLabel(p);
          existing.policyId = p.id;
          existing.policyCurrentOrUpcoming = policyCurrentOrUpcoming;
          existing.active = isActive;
          existing.removedAt = removedAt;
        }
        continue;
      }
      const entry: VehicleEntry = {
        vehicle: t,
        policyNumber: p.policyNumber,
        policyTermLabel: policyTermLabel(p),
        policyId: p.id,
        policyCurrentOrUpcoming,
        active: isActive,
        removedAt,
      };
      if (t.vin) seen.set(t.vin, entry);
      results.push(entry);
    }
  }
  results.sort(sortByLifecycle);
  return results;
}

/**
 * CSR-1345 rewrite — group driver rows (within and across policies) into one
 * entry per physical driver, converging masked and unmasked variants.
 *
 * Grouping evidence (`driverVariantAffinity`, shared with the server merge):
 * a masked license suffix matching its unmasked counterpart ("*****8491" <->
 * "051638491") or a masked DOB year matching the real DOB year joins; positive
 * conflicts (different DOB years, incompatible licenses, differing
 * databaseIds) NEVER join — so two legitimate same-name drivers stay separate.
 * Evidence-less rows fall back to a name join only when it is unambiguous
 * (exactly one same-name group), preserving the CSR-1093 cross-policy dedup.
 *
 * The displayed `driver` is a field-level best across the group (real values
 * beat masked beat empty), and lifecycle prefers active > inactive > removed.
 */
export function aggregateDrivers(policies: Policy[]): DriverEntry[] {
  interface RowCtx {
    driver: PolicyDriver;
    policyNumber: string;
    policyTermLabel: string;
    policyId: string;
    policyCurrentOrUpcoming: boolean;
    active: boolean;
    removedAt: string | null;
    index: number;
  }

  const rows: RowCtx[] = [];
  for (const p of policies) {
    const drivers = p.drivers as PolicyDriver[] | null;
    if (!Array.isArray(drivers)) continue;
    const policyCurrentOrUpcoming = policyIsCurrentOrUpcoming(p);
    for (const d of drivers) {
      const removedAt = d.removedAt ?? null;
      rows.push({
        driver: d,
        policyNumber: p.policyNumber,
        policyTermLabel: policyTermLabel(p),
        policyId: p.id,
        policyCurrentOrUpcoming,
        active: policyCurrentOrUpcoming && !removedAt,
        removedAt,
        index: rows.length,
      });
    }
  }

  // Evidence-rich rows seed groups first so masked/empty variants join an
  // existing real-valued group instead of seeding their own.
  const richness = (d: PolicyDriver): number =>
    (hasRealDriverValue(d.licenseNumber) ? 2 : d.licenseNumber?.trim() ? 1 : 0) +
    (hasRealDriverValue(d.dateOfBirth) ? 2 : d.dateOfBirth?.trim() ? 1 : 0) +
    (d.databaseId?.trim() ? 1 : 0);
  const ordered = [...rows].sort(
    (a, b) => richness(b.driver) - richness(a.driver) || a.index - b.index,
  );

  interface Group {
    members: RowCtx[];
    firstIndex: number;
  }
  const groups: Group[] = [];

  const groupAffinity = (d: PolicyDriver, group: Group): FieldAffinity => {
    let best: FieldAffinity = "neutral";
    for (const member of group.members) {
      const affinity = driverVariantAffinity(d, member.driver);
      if (affinity === "conflict") return "conflict";
      if (affinity === "match") best = "match";
    }
    return best;
  };

  for (const row of ordered) {
    let target = groups.find((g) => groupAffinity(row.driver, g) === "match");
    if (!target) {
      const key = keyForDriverDisplay(row.driver);
      if (key) {
        const candidates = groups.filter(
          (g) =>
            g.members.some((m) => keyForDriverDisplay(m.driver) === key) &&
            groupAffinity(row.driver, g) !== "conflict",
        );
        // Join by name/key only when unambiguous — two legitimate same-name
        // drivers (conflicting evidence) each keep their own row.
        if (candidates.length === 1) target = candidates[0];
      }
    }
    if (target) {
      target.members.push(row);
      target.firstIndex = Math.min(target.firstIndex, row.index);
    } else {
      groups.push({ members: [row], firstIndex: row.index });
    }
  }

  const results: DriverEntry[] = groups
    .sort((a, b) => a.firstIndex - b.firstIndex)
    .map((group) => {
      // The current term decides a duplicate's lifecycle; first-seen breaks ties.
      const best = group.members.reduce((acc, m) =>
        selectionRank(m) > selectionRank(acc) || (selectionRank(m) === selectionRank(acc) && m.index < acc.index) ? m : acc,
      );
      // Field-level best values: fold the variants into the winner's row,
      // richest evidence first so real values claim the gaps.
      const merged = group.members
        .filter((m) => m !== best)
        .sort((a, b) => richness(b.driver) - richness(a.driver) || a.index - b.index)
        .reduce((acc, m) => mergeDriverVariantFields(acc, m.driver), { ...best.driver });
      return {
        driver: merged,
        policyNumber: best.policyNumber,
        policyTermLabel: best.policyTermLabel,
        policyId: best.policyId,
        policyCurrentOrUpcoming: best.policyCurrentOrUpcoming,
        active: best.active,
        removedAt: best.removedAt,
        sourceRows: group.members
          .sort((a, b) => a.index - b.index)
          .map((m) => ({ policyId: m.policyId, driver: m.driver })),
      };
    });

  results.sort(sortByLifecycle);
  return results;
}

/**
 * When the same physical asset appears on multiple policies in different
 * lifecycle states, prefer to display the "best" one: active > inactive > removed.
 * Returns true when `candidate` is strictly better than the entry already stored.
 */
function preferEntry(
  existing: { active: boolean; removedAt: string | null; policyCurrentOrUpcoming?: boolean },
  candidate: { active: boolean; removedAt: string | null; policyCurrentOrUpcoming: boolean },
): boolean {
  return selectionRank(candidate) > selectionRank(existing);
}

function selectionRank({
  active,
  removedAt,
  policyCurrentOrUpcoming,
}: {
  active: boolean;
  removedAt: string | null;
  policyCurrentOrUpcoming?: boolean;
}): number {
  const live = policyCurrentOrUpcoming ?? active;
  if (live && !removedAt) return 3;
  if (live) return 2;
  if (!removedAt) return 1;
  return 0;
}

function lifecycleRank({ active, removedAt }: { active: boolean; removedAt: string | null }): number {
  if (active) return 2;
  if (!removedAt) return 1;
  return 0;
}

function sortByLifecycle(
  a: { active: boolean; removedAt: string | null; policyCurrentOrUpcoming?: boolean },
  b: { active: boolean; removedAt: string | null; policyCurrentOrUpcoming?: boolean },
): number {
  return lifecycleRank(b) - lifecycleRank(a);
}

/**
 * Transitional safety net for CSR-665. After the trailers/vehicles split landed,
 * the primary read path sources trailers from `policy.trailers` directly. These
 * helpers remain exported only to handle pre-CSR-665 rows whose trailers still
 * sit inside `policy.vehicles` until the next sync re-populates `policy.trailers`.
 * Do not wire them into new read paths.
 */
export function isTrailer(entry: VehicleEntry): boolean {
  return (entry.vehicle.truckType ?? "").trim().toLowerCase() === TRAILER_TRUCK_TYPE.toLowerCase();
}

/**
 * Transitional safety net for CSR-665. See `isTrailer` above.
 */
export function splitVehicles(vehicles: VehicleEntry[]): {
  powerUnits: VehicleEntry[];
  trailers: VehicleEntry[];
} {
  const powerUnits: VehicleEntry[] = [];
  const trailers: VehicleEntry[] = [];
  for (const v of vehicles) {
    if (isTrailer(v)) trailers.push(v);
    else powerUnits.push(v);
  }
  return { powerUnits, trailers };
}

export { isNonOwnedAttachedTrailer } from "@shared/coi-vehicles";

export function formatMakeModel(vehicle: PolicyVehicle): string {
  const parts = [vehicle.make, vehicle.model].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

// CSR-789: Stated value display. Prefers the top-level `value` field populated
// by the data-center Progressive sync (CSR-757); falls back to NowCerts'
// `vehicleSpecificCoverages` shape (Comprehensive limits[0], else Collision)
// so legacy rows light up immediately without waiting for a re-sync.
export function vehicleValue(v: PolicyVehicle): string {
  const top = v.value;
  if (top !== undefined && top !== null && String(top).trim() !== "")
    return formatCurrency(top);
  const wide = v as {
    vehicleSpecificCoverages?: Array<{ name?: string; limits?: unknown[] }>;
  };
  const coverages = Array.isArray(wide.vehicleSpecificCoverages)
    ? wide.vehicleSpecificCoverages
    : [];
  const pick = (name: string) =>
    coverages.find((c) => (c?.name || "").toLowerCase() === name);
  const limit =
    pick("comprehensive")?.limits?.[0] ?? pick("collision")?.limits?.[0];
  return typeof limit === "number" ? formatCurrency(limit) : "—";
}

function formatCurrency(amount: string | number): string {
  const n =
    typeof amount === "number"
      ? amount
      : parseFloat(String(amount).replace(/[^\d.-]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}
